import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { workflowAPI } from '../../lib/api';
import { errMsg, timeAgo } from '../../lib/workflow';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const KIND = {
  note: { emoji: '📝', cls: 'bg-[#FFF8EC] border-[#F6E3C0]' },
  changes: { emoji: '🔁', cls: 'bg-[#FFF1F2] border-[#FECDD3]' },
  review_note: { emoji: '🚀', cls: 'bg-[#F3E8FF]/60 border-[#E9D5FF]' },
  approved: { emoji: '✅', cls: 'bg-[#EBF5F0] border-[#C5E3D5]' },
  comment: { emoji: '💬', cls: 'bg-[#FDFBF7] border-[#F0EAE1]' },
};

export const CommentThread = ({ item, canComment, onChanged }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [all, setAll] = useState(false);
  const comments = item.workflow?.comments || [];
  const shown = all ? comments : comments.slice(-3);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await workflowAPI.comment(item.id, text);
      setText('');
      onChanged?.(r.data);
    } catch (e) {
      toast.error(errMsg(e, 'Could not post comment'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="workflow-comments">
      {comments.length > 3 && (
        <button onClick={() => setAll((v) => !v)} className="text-[11px] font-semibold text-[#C85A32] hover:underline" data-testid="workflow-comments-toggle">
          {all ? 'Show latest only' : `Show all ${comments.length} messages`}
        </button>
      )}
      {shown.map((c) => {
        const k = KIND[c.kind] || KIND.comment;
        return (
          <div key={c.id} className={`rounded-2xl border px-3 py-2 text-xs text-[#2D2623] ${k.cls}`} data-testid={`workflow-comment-${c.id}`}>
            <div className="flex items-center justify-between gap-2 text-[11px] text-[#9A8E85] mb-0.5">
              <span className="font-bold text-[#6E635B]">{k.emoji} {c.by_name}</span>
              <span>{timeAgo(c.at)}</span>
            </div>
            <p className="whitespace-pre-line">{c.text}</p>
          </div>
        );
      })}
      {canComment && (
        <div className="flex items-center gap-2">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder="💬 Add a comment for the team…"
            className="h-9 rounded-xl border-[#E8E1D5] bg-white text-xs"
            data-testid="workflow-comment-input"
          />
          <Button onClick={send} disabled={busy || !text.trim()} className="h-9 rounded-xl bg-[#3D405B] hover:bg-[#2F3248] text-white text-xs" data-testid="workflow-comment-send-btn">
            {busy ? <Loader2 size={12} className="animate-spin" /> : 'Send'}
          </Button>
        </div>
      )}
    </div>
  );
};
